package twist.mena.sports.admin.components

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.files.File
import twist.mena.sports.admin.theme.*

@Composable
fun FormField(label: String, content: @Composable () -> Unit) {
    val colors = AppTheme.colors
    Div({
        style { marginBottom(Spacing.lg) }
    }) {
        Div({
            style {
                display(DisplayStyle.Block)
                fontSize(Typography.base)
                fontWeight("600")
                color(Color(colors.text))
                marginBottom(Spacing.sm)
                themeTransition()
            }
        }) { Text(label) }
        content()
    }
}

@Composable
fun ImageUpload(
    currentImageUrl: String?,
    entityId: String,
    onUpload: suspend (File) -> Unit,
    onRemove: suspend () -> Unit
) {
    val colors = AppTheme.colors
    var isUploading by remember { mutableStateOf(false) }
    var uploadError by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    val inputId = "image-upload-$entityId"

    FormField("Image") {
        Div({
            style {
                display(DisplayStyle.Flex)
                property("flex-direction", "column")
                property("gap", "12px")
            }
        }) {
            if (currentImageUrl != null) {
                Img(currentImageUrl) {
                    style {
                        width(120.px)
                        height(120.px)
                        borderRadius(BorderRadius.lg)
                        property("object-fit", "cover")
                        border(1.px, LineStyle.Solid, Color(colors.border))
                    }
                }
            } else {
                Div({
                    style {
                        width(120.px)
                        height(120.px)
                        backgroundColor(Color(colors.surfaceHover))
                        borderRadius(BorderRadius.lg)
                        display(DisplayStyle.Flex)
                        property("align-items", "center")
                        property("justify-content", "center")
                        color(Color(colors.textMuted))
                        fontSize(Typography.sm)
                        border(2.px, LineStyle.Dashed, Color(colors.border))
                    }
                }) {
                    Text("No image")
                }
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    property("gap", "8px")
                    property("align-items", "center")
                }
            }) {
                Input(InputType.File) {
                    id(inputId)
                    attr("accept", "image/*")
                    if (isUploading) disabled()
                    onChange { event ->
                        val input = event.target as org.w3c.dom.HTMLInputElement
                        val file = input.files?.item(0)
                        if (file != null) {
                            scope.launch {
                                isUploading = true
                                uploadError = null
                                try {
                                    onUpload(file.unsafeCast<File>())
                                    input.value = ""
                                } catch (e: Exception) {
                                    uploadError = e.message ?: "Upload failed"
                                } finally {
                                    isUploading = false
                                }
                            }
                        }
                    }
                    style { display(DisplayStyle.None) }
                }

                Button({
                    attr("type", "button")
                    if (isUploading) disabled()
                    onClick { event ->
                        event.preventDefault()
                        kotlinx.browser.document.getElementById(inputId)?.asDynamic()?.click()
                    }
                    style {
                        padding(Spacing.sm, Spacing.lg)
                        backgroundColor(Color(colors.primary))
                        color(Color("#ffffff"))
                        border(0.px)
                        borderRadius(BorderRadius.md)
                        fontSize(Typography.base)
                        fontWeight("500")
                        cursor(if (isUploading) "not-allowed" else "pointer")
                        property("transition", "background-color 0.2s")
                        if (isUploading) opacity(0.6)
                    }
                }) {
                    Text(
                        when {
                            isUploading -> "Uploading..."
                            currentImageUrl != null -> "Change Image"
                            else -> "Upload Image"
                        }
                    )
                }

                if (currentImageUrl != null && !isUploading) {
                    Button({
                        onClick {
                            scope.launch {
                                isUploading = true
                                uploadError = null
                                try {
                                    onRemove()
                                } catch (e: Exception) {
                                    uploadError = e.message ?: "Failed to remove image"
                                } finally {
                                    isUploading = false
                                }
                            }
                        }
                        style {
                            padding(Spacing.sm, Spacing.lg)
                            dangerButtonStyle(colors)
                        }
                    }) {
                        Text("Remove")
                    }
                }
            }

            Div({
                style {
                    fontSize(Typography.sm)
                    color(Color(colors.textMuted))
                }
            }) {
                Text("Accepted formats: JPG, PNG, WebP (max 5MB)")
            }

            if (isUploading) {
                Div({
                    style {
                        fontSize(Typography.base)
                        color(Color(colors.primary))
                        fontWeight("500")
                    }
                }) { Text("Processing...") }
            }

            uploadError?.let { error ->
                Div({
                    style { errorMessageStyle(colors) }
                }) { Text(error) }
            }
        }
    }
}

@Composable
fun ConfirmationDialog(
    title: String,
    message: String,
    onConfirm: () -> Unit,
    onCancel: () -> Unit
) {
    val colors = AppTheme.colors
    Div({
        style { confirmationOverlayStyle(colors) }
    }) {
        Div({
            style { confirmationContentStyle(colors) }
            onClick { it.stopPropagation() }
        }) {
            H4({
                style {
                    margin(0.px, 0.px, Spacing.md, 0.px)
                    fontSize(Typography.xl)
                    fontWeight("600")
                }
            }) { Text(title) }

            P({
                style {
                    margin(0.px, 0.px, Spacing.xl, 0.px)
                    color(Color(colors.textMuted))
                }
            }) { Text(message) }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.End)
                    gap(Spacing.md)
                }
            }) {
                Button({
                    onClick { onCancel() }
                    style { secondaryButtonStyle(colors) }
                }) { Text("Cancel") }

                Button({
                    onClick { onConfirm() }
                    style { primaryButtonStyle(colors) }
                }) { Text("Confirm") }
            }
        }
    }
}
